import { PieChart } from '@mui/icons-material';
import { Box, Grid, MenuItem, Select } from '@mui/material';
import {
  getDebtorConcentrations,
  getFundsConcentrations,
} from 'lib/api/concentrations';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { exportToXlsx } from './exportToExcel';

const TOP_CONCENTRATIONS_COUNT = 5;

type ConcentrationType = 'byCompany' | 'bySector' | 'bySalesSegment';

const MyWalletConcentrations = () => {
  const [debtorType, setDebtorType] = useState<ConcentrationType>('byCompany');
  const [clientType, setClientType] = useState<ConcentrationType>('byCompany');
  const [debtorTopN, setDebtorTopN] = useState<number>(5);
  const [clientTopN, setClientTopN] = useState<number>(5);
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(1, 'months').format('MM/DD/YYYY'),
  );
  const dispatch = useDispatch();
  const {
    data: concentrations,
    isPending: isPendingConcentrations,
    error: errorConcentrations,
  } = getDebtorConcentrations(dispatch, startDate);
  const {
    data: fundsConcentrations,
    isPending: isPendingFunds,
    error: errorFunds,
  } = getFundsConcentrations(dispatch, startDate);

  const processConcentrations = (
    data: { name: string; value: number }[],
    topN: number = TOP_CONCENTRATIONS_COUNT,
  ) => {
    if (!data || data.length === 0) return [];

    const sorted = [...data].sort((a, b) => b.value - a.value);
    const topNItems = sorted.slice(0, topN);

    if (sorted.length > topN) {
      const others = sorted.slice(topN);
      const othersTotal = others.reduce((sum, item) => sum + item.value, 0);
      return [...topNItems, { name: 'Otros', value: othersTotal }];
    }
    return topNItems;
  };

  const debtorConcentrations = useMemo(
    () =>
      processConcentrations(
        concentrations?.debtor[debtorType] || [],
        debtorTopN,
      ),
    [concentrations, debtorType, debtorTopN],
  );

  const clientConcentrations = useMemo(
    () =>
      processConcentrations(
        concentrations?.client[clientType] || [],
        clientTopN,
      ),
    [concentrations, clientType, clientTopN],
  );

  const isPending = isPendingConcentrations || isPendingFunds;
  const error = errorConcentrations ?? errorFunds;

  const handleExport = async () => {
    await exportToXlsx(concentrations, fundsConcentrations);
  };

  useEffect(() => {
    if (error) {
      errorHandler(dispatch, error, 'Error al obtener las concentraciones');
    }
  }, [error]);

  if (isPending) return <CenteredCircularProgress />;

  return (
    <Box mt={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        mx={2}
      >
        <Title
          title="Concentraciones"
          icon={<PieChart color="primary" sx={{ fontSize: 20 }} />}
          color="primary"
        />
        <Grid container spacing={2} direction="row" width="auto">
          <Grid item>
            <Select
              label="Fecha de inicio"
              defaultValue={startDate}
              size="small"
              onChange={(e) => setStartDate(e.target.value)}
            >
              <MenuItem
                value={moment().subtract(1, 'month').format('MM/DD/YYYY')}
              >
                Último mes
              </MenuItem>
              <MenuItem
                value={moment().subtract(3, 'months').format('MM/DD/YYYY')}
              >
                Últimos 3 meses
              </MenuItem>
              <MenuItem
                value={moment().subtract(6, 'months').format('MM/DD/YYYY')}
              >
                Últimos 6 meses
              </MenuItem>
              <MenuItem
                value={moment().subtract(12, 'months').format('MM/DD/YYYY')}
              >
                Últimos 12 meses
              </MenuItem>
            </Select>
          </Grid>

          <Grid item>
            <CustomButton color="primary" onClick={handleExport}>
              Exportar
            </CustomButton>
          </Grid>
        </Grid>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Por deudor"
              tooltipContent="Concentraciones por deudor"
              tooltipPlacement="right"
              variant="compact"
            >
              <Box display="flex" gap={2} mb={2}>
                <Select
                  label="Tipo de concentrado"
                  value={debtorType}
                  size="small"
                  onChange={(e) =>
                    setDebtorType(e.target.value as ConcentrationType)
                  }
                >
                  <MenuItem value="byCompany">Por empresa</MenuItem>
                  <MenuItem value="bySector">Por sector</MenuItem>
                  <MenuItem value="bySalesSegment">
                    Por segmento de ventas
                  </MenuItem>
                </Select>
                <Select
                  label="Top N"
                  value={debtorTopN}
                  size="small"
                  onChange={(e) => setDebtorTopN(Number(e.target.value))}
                >
                  {Array.from({ length: 25 }, (_, i) => i + 1).map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </SubtitleWithTooltip>
            <CentralPieChart data={debtorConcentrations} />
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Por cliente"
              tooltipContent="Concentraciones por cliente"
              tooltipPlacement="right"
              variant="compact"
            >
              <Box display="flex" gap={2} mb={2}>
                <Select
                  label="Tipo de concentrado"
                  value={clientType}
                  size="small"
                  onChange={(e) =>
                    setClientType(e.target.value as ConcentrationType)
                  }
                >
                  <MenuItem value="byCompany">Por empresa</MenuItem>
                  <MenuItem value="bySector">Por sector</MenuItem>
                  <MenuItem value="bySalesSegment">
                    Por segmento de ventas
                  </MenuItem>
                </Select>
                <Select
                  label="Top N"
                  value={clientTopN}
                  size="small"
                  onChange={(e) => setClientTopN(Number(e.target.value))}
                >
                  {Array.from({ length: 25 }, (_, i) => i + 1).map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </SubtitleWithTooltip>
            <CentralPieChart data={clientConcentrations} />
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Por fondos"
              tooltipContent="Concentraciones por fondos"
              tooltipPlacement="right"
              variant="compact"
            ></SubtitleWithTooltip>
            <CentralPieChart data={fundsConcentrations?.byCompany || []} />
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyWalletConcentrations;
